import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import './form.css'

/**
 * Formularbausteine.
 *
 * `FormField` verdrahtet Label, Hilfetext und Fehlermeldung ueber IDs mit dem
 * Eingabefeld. Ein Fehler, der nur rot ist, erreicht niemanden, der die Seite
 * vorgelesen bekommt — deshalb ist aria-describedby hier nicht optional.
 */

export interface FormFieldProps {
  label: string
  hint?: string
  error?: string | null
  required?: boolean
  /** Erhaelt id und die aria-Attribute; damit umschliesst es jedes Eingabefeld. */
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': boolean | undefined
    required: boolean | undefined
  }) => ReactNode
}

export function FormField({
  label,
  hint,
  error,
  required,
  children,
}: FormFieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="field" data-invalid={error ? true : undefined}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required: required || undefined,
      })}

      {hint && !error && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      className={['control', className].filter(Boolean).join(' ')}
      {...rest}
    />
  )
})

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, rows = 4, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={['control', 'control--textarea', className].filter(Boolean).join(' ')}
        {...rest}
      />
    )
  },
)

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <div className="control-wrap">
      <select
        ref={ref}
        className={['control', 'control--select', className].filter(Boolean).join(' ')}
        {...rest}
      >
        {children}
      </select>
      <span className="control-wrap__chevron" aria-hidden="true">
        <svg viewBox="0 0 12 12" fill="none">
          <path
            d="M2.5 4.5 6 8l3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  )
})

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className, ...rest },
  ref,
) {
  return (
    <label className={['choice', className].filter(Boolean).join(' ')}>
      <input ref={ref} type="checkbox" className="choice__input" {...rest} />
      <span className="choice__box" aria-hidden="true">
        <svg viewBox="0 0 12 12" fill="none">
          <path
            d="M2.5 6.2 4.8 8.5 9.5 3.8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="choice__label">{label}</span>
    </label>
  )
})

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(function Switch(
  { label, className, ...rest },
  ref,
) {
  return (
    <label className={['switch', className].filter(Boolean).join(' ')}>
      <input ref={ref} type="checkbox" role="switch" className="switch__input" {...rest} />
      <span className="switch__track" aria-hidden="true">
        <span className="switch__thumb" />
      </span>
      <span className="switch__label">{label}</span>
    </label>
  )
})
